<template>
    <v-form ref="form" v-model="valid" data-cy="client-form">
        <v-row>
            <v-col cols="12" md="9">
                <v-row>
                    <v-col cols="12" md="3">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('personal_information') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-select
                                    v-model="person.prefix"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="$t('salutation')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="prefix"
                                />
                            </v-col>
                            <v-col cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-text-field
                                    v-model="person.firstName"
                                    :label="`${$t('first_name')}*`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[requiredRule('first_name')]"
                                    data-cy="first-name"
                                />
                            </v-col>
                            <v-col v-if="isShowMiddleName" cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-text-field
                                    v-model="person.middleName"
                                    :label="`${$t('middle_name')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="middle-name"
                                />
                            </v-col>
                            <v-col cols="12" :sm="isShowMiddleName ? 3 : 4">
                                <v-text-field
                                    v-model="person.lastName"
                                    :label="`${$t('last_name')}*`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[requiredRule('last_name')]"
                                    data-cy="last-name"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-checkbox
                                    v-model="person.isLatinTheSame"
                                    :label="$t('name_as_latin')"
                                    density="compact"
                                    color="primary"
                                    data-cy="is-latin-name-the-same"
                                />
                            </v-col>
                            <v-col v-if="!person.isLatinTheSame" cols="12" sm="4">
                                <v-text-field
                                    v-model="person.foreignFirstName"
                                    :label="`${$t('first_name')} (LATIN)`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="foreign-first-name"
                                    :rules="[latinRule('first_name')]"
                                />
                            </v-col>
                            <v-col v-if="!person.isLatinTheSame" cols="12" sm="4">
                                <v-text-field
                                    v-model="person.foreignLastName"
                                    :label="`${$t('last_name')} (LATIN)`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="foreign-last-name"
                                    :rules="[latinRule('last_name')]"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="person.contactPhone"
                                    :label="$t('phone')"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-phone"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="person.secondContactPhone"
                                    :label="`${$t('phone')} 2`"
                                    density="compact"
                                    color="primary"
                                    data-cy="second-contact-phone"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="person.thirdContactPhone"
                                    :label="`${$t('phone')} 3`"
                                    density="compact"
                                    color="primary"
                                    data-cy="third-contact-phone"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.email"
                                    :label="$t('email')"
                                    type="email"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="email"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.secondEmail"
                                    :label="`${$t('email')} 2`"
                                    type="email"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="second-email"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.thirdEmail"
                                    :label="`${$t('email')} 3`"
                                    type="email"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="third-email"
                                />
                            </v-col>
                        </v-row>
                        <div class="text-subtitle-1 font-italic py-4">{{ $t('clients_page.info_contact_note') }}</div>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.fax"
                                    :label="`${$t('fax')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="fax"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.postalCode"
                                    :label="`${$t('postal_code')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="postal-code"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.addressLine"
                                    :label="`${$t('address')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="address-line"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-autocomplete
                                    v-model="person.countryId"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    :label="`${$t('country')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="country-id"
                                    @update:model-value="loadCities"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-autocomplete
                                    v-model="person.cityId"
                                    :items="cities"
                                    item-title="name"
                                    item-value="id"
                                    :label="`${$t('city')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="city-id"
                                />
                            </v-col>
                            <v-col v-if="!hideCitizenship" cols="12" sm="4">
                                <v-autocomplete
                                    v-model="person.citizenshipId"
                                    :label="$t('citizenship')"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="citizenship-id"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('additional_information') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <date-picker
                                    v-model="person.birthdate"
                                    :label="$t('birthdate')"
                                    :max="$dateFns.format(new Date(), 'yyyy-MM-dd')"
                                    density="compact"
                                    color="primary"
                                    year-active-picker
                                    outlined
                                    dense
                                    data-cy="birthdate"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.birthplace"
                                    :label="`${$t('birthplace')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="birthplace"
                                />
                            </v-col>
                        </v-row>
                        <h5 class="text-subtitle-1 pb-1">
                            {{ $t('travel_preferences') }}
                        </h5>
                        <v-textarea
                            v-model="person.travelPreferences"
                            variant="outlined"
                            rows="2"
                            color="primary"
                            data-cy="travel-preferences"
                        />
                        <h5 class="text-subtitle-1 pb-1">
                            {{ $t('allergy') }}
                        </h5>
                        <v-textarea
                            v-model="person.allergy"
                            variant="outlined"
                            rows="2"
                            color="primary"
                            data-cy="allergy"
                        />
                        <h5 class="text-subtitle-1 pb-1">
                            {{ $t('disability') }}
                        </h5>
                        <v-textarea
                            v-model="person.disability"
                            variant="outlined"
                            rows="2"
                            color="primary"
                            data-cy="disability"
                        />
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.company"
                                    :label="`${$t('company')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="company"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.position"
                                    :label="`${$t('position')}`"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="position"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('emergency_contact') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <v-select
                                    v-model="person.emergencyContactPrefix"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="$t('salutation')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="emergency-contact-prefix"
                                    clearable
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.emergencyContactFirstName"
                                    :label="$t('first_name')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="emergency-contact-first-name"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.emergencyContactLastName"
                                    :label="$t('last_name')"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    data-cy="emergency-contact-last-name"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" sm="4">
                                <phone-field
                                    v-model="person.emergencyContactPhone"
                                    :label="$t('phone')"
                                    density="compact"
                                    color="primary"
                                    data-cy="emergency-contact-phone"
                                />
                            </v-col>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    v-model="person.emergencyContactEmail"
                                    :label="`${$t('email')} 2`"
                                    type="email"
                                    density="compact"
                                    variant="outlined"
                                    color="primary"
                                    :rules="[emailRule('email')]"
                                    data-cy="emergency-contact-email"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="text-h6 text--secondary">
                        <h4 class="text-h6 text--secondary">
                            {{ $t('comment') }}
                        </h4>
                    </v-col>
                    <v-col cols="12" md="9">
                        <div class="px-0 font-italic text-subtitle-2">
                            {{ $t('enter_comment') }}
                        </div>
                        <v-textarea
                            v-model="person.comments"
                            variant="outlined"
                            color="primary"
                            data-cy="person-comments"
                        />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="3" class="order-first order-md-last">
                <v-row v-if="person.personId" dense>
                    <div v-if="person.personId" class="text-subtitle-2 text-grey font-weight-regular ml-auto px-4">
                        {{ `${$t('id')}: ${person.personId}` }}
                    </div>
                </v-row>
                <v-row v-if="person.personId" dense class="px-4">
                    <v-switch
                        v-model="person.active"
                        color="primary"
                        class="ml-auto"
                        density="compact"
                        hide-details
                        :label="person.active ? $t('active') : $t('inactive')"
                        data-cy="person-active"
                    />
                </v-row>
                <h4 class="text-h6 text--secondary py-3">
                    {{ person.personId ? $t('change_password') : $t('password') }}
                </h4>
                <v-row dense>
                    <v-col>
                        <v-text-field
                            v-model="password"
                            :label="`${$t('password')}${
                                person.personId || isMember || isTourOperator || isAgency ? '' : '*'
                            }`"
                            type="password"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            :rules="[
                                v =>
                                    !!v ||
                                    !!person.personId ||
                                    isMember ||
                                    isTourOperator ||
                                    isAgency ||
                                    `${$t('password')} ${$t('validation.required')}`,
                            ]"
                            data-cy="password"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-text-field
                            v-model="passwordToMatch"
                            :label="`${$t('password_confirm')}${
                                person.personId || isMember || isTourOperator || isAgency ? '' : '*'
                            }`"
                            type="password"
                            autocomplete="new-password"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            :rules="[
                                v =>
                                    password === v ||
                                    !password ||
                                    `${$t('password_confirm')} ${$t('validation.match')}`,
                            ]"
                            data-cy="password-confirm"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-autocomplete
                            v-model="person.b2CId"
                            :item-title="getB2CName"
                            item-value="b2cId"
                            :items="b2cs"
                            :label="'B2C'"
                            :loading="isLoadingData"
                            :disabled="isLoadingData"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="b2c-id"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-autocomplete
                            v-model="person.managerId"
                            item-title="name"
                            item-value="personId"
                            :items="users"
                            :label="$t('manager')"
                            :loading="isLoadingData"
                            :disabled="isLoadingData"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="manager-id"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-select
                            v-model="person.language"
                            :items="locales"
                            :label="$t('language')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="language"
                        />
                    </v-col>
                </v-row>
                <h4 class="text-h6 text-capitalize pb-2">
                    {{ $t('avatar') }}
                </h4>
                <v-row>
                    <div class="relative-position px-2 pt-4 pb-10">
                        <v-avatar color="grey" size="150">
                            <v-img
                                v-if="person.avatar || avatar"
                                :src="avatar ? avatar : $filters.imageUrl(person.avatar, 300)"
                                cover
                            />
                        </v-avatar>
                        <div style="position: absolute; bottom: 40px; right: -20px">
                            <v-file-input
                                v-model="file"
                                prepend-icon="mdi-pencil"
                                small-chips
                                variant="outlined"
                                density="compact"
                                accept="image/jpg, image/jpeg, image/png"
                                hide-details
                                :label="$t('select_photo')"
                                data-cy="avatar"
                                class="hide-input"
                                @update:model-value="onPhotoLoad"
                            />
                        </div>
                    </div>
                </v-row>
                <v-row v-if="isCRMExtendedModule && !isAgency && !isTO2" dense>
                    <v-col>
                        <v-select
                            v-model="person.stage"
                            :items="stages"
                            :label="$t('stage')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="stage"
                        />
                    </v-col>
                </v-row>
                <v-row v-if="isCRMExtendedModule && !isAgency && !isTO2" dense>
                    <v-col>
                        <v-select
                            v-model="person.priority"
                            :items="priority"
                            :label="$t('priority')"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="priority"
                        />
                    </v-col>
                </v-row>
                <v-row v-if="isCRMExtendedModule" dense>
                    <v-col>
                        <v-combobox
                            v-model="labels"
                            :label="$t('labels')"
                            chips
                            multiple
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="labels"
                        >
                            <template v-slot:chip="{attrs, item}">
                                <v-chip v-bind="attrs" closable color="primary" @click:close="removeLabel(item.value)">
                                    <strong>{{ item.title }}</strong>
                                </v-chip>
                            </template>
                        </v-combobox>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-select
                            v-model="person.memberLevel"
                            :item-title="getMembershipLevel"
                            item-value="id"
                            :items="membershipLevels"
                            :label="$t('membership_level')"
                            :loading="isLoadingData"
                            :disabled="isLoadingData"
                            clearable
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="member-level-id"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <date-picker
                            v-model="person.expirationDate"
                            :label="$t('expiration_date')"
                            density="compact"
                            color="primary"
                            year-active-picker
                            outlined
                            dense
                            data-cy="expiration-date"
                        />
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col>
                        <v-text-field
                            v-model="person.externalMemberID"
                            :label="$t('external_member_id')"
                            type="number"
                            hide-spin-buttons
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="external-member-id"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-row no-gutters class="gc-4">
            <v-btn :loading="isLoading" :disabled="isLoading" data-cy="save" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="cancel" variant="text" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Inject, mixins, Prop, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'

    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import PhoneField from '@/components/snippets/forms/PhoneField.vue'
    import type {VForm} from 'vuetify/components'
    import {Priority, type SelectOptionType, Stage} from '~/utils/types/types'
    import {clone, clean, blobToBase64} from '~/utils/helpers'

    @Component({
        components: {PhoneField, DatePicker},
    })
    export class ClientForm extends mixins(FormValidationRulesMixin) {
        @Prop() personData
        @Prop({default: false, type: Boolean}) isMember: boolean
        @Inject() goTo

        valid: boolean = false
        isLoading: boolean = false
        password: string = null
        passwordToMatch: string = null
        isLoadingData: boolean = false
        cities = []
        b2cs = []
        users = []
        labels = []
        membershipLevels = []

        avatar: string = null
        file = null
        person = {}

        @Watch('password')
        onChangePassword() {
            this.form.validate()
        }

        async mounted() {
            await this.initData()
            this.person = clone(this.personData)

            // /api/privateClients serializes values as strings (e.g. active === 'true'),
            // so numeric ids arrive as strings. Coerce to Number so v-autocomplete strict
            // equality against numeric item ids (cities/countries) matches and the name shows.
            ;['cityId', 'countryId', 'citizenshipId'].forEach((key) => {
                if (this.person[key] != null) this.person[key] = Number(this.person[key])
            })

            if (this.person?.labels) this.labels = this.person.labels.split(',')
            if (this.person.personId) {
                await this.loadCities()
            }
        }

        get form(): VForm {
            return this.$refs.form as VForm
        }

        async onPhotoLoad() {
            if (!this.file) {
                this.avatar = null
                return
            }
            const file = Array.isArray(this.file) ? this.file[0] : this.file
            this.avatar = await blobToBase64(file)
        }

        getB2CName({name, id}): string {
            return `${name || `id: ${id}`}`
        }

        get locales(): SelectOptionType[] {
            return Object.values(this.$conf.public.locales).map(lang => {
                return {value: lang, title: this.$t(`languages.${lang}`)}
            })
        }

        get stages(): SelectOptionType[] {
            const stages: Stage[] = [Stage.Client, Stage.Potential]
            return stages.map(value => ({value, title: this.$t(`crmStage.${value}`)}))
        }

        get priority(): SelectOptionType[] {
            const priorities: Priority[] = [Priority.VIP, Priority.High, Priority.Normal, Priority.Low]
            return priorities.map(value => ({value, title: this.$t(`crmPriority.${value}`)}))
        }

        getMembershipLevel({code}): string {
            return this.$t(`crm_membership_level.${code}`)
        }

        async initData(): Promise<void> {
            this.isLoadingData = true
            try {
                await Promise.all([
                    (async () => {
                        this.b2cs = (await this.$api.b2c.get()).b2cs.sort((a, b) => a.name.localeCompare(b.name))
                    })(),
                    (async () => {
                        this.users = (await this.$api.persons.get({companyId: authStore.company.id})).users
                            .map(({personId, firstName, lastName}) => ({
                                personId,
                                name: `${lastName || ''} ${firstName || ''}`.trim(),
                            }))
                            .sort((a, b) => a.name.localeCompare(b.name))
                    })(),
                    (async () => {
                        this.membershipLevels = (await this.$api.membershipLevels.get()).membershipLevels
                    })(),
                ])
            } catch (e) {
                this.b2cs = []
                this.users = []
                this.membershipLevels = []
            } finally {
                this.isLoadingData = false
            }
        }

        cancel() {
            const routeName = this.isMember ? 'members' : 'companyClients'
            this.$router.push({name: routeName})
        }

        async save() {
            const {errors} = await this.form.validate()
            if (!this.valid) {
                this.goTo(document.getElementById(errors[0].id))
                return
            }
            this.isLoading = true
            try {
                const necessaryKeys = [
                    'prefix',
                    'isLatinTheSame',
                    'foreignFirstName',
                    'foreignLastName',
                    'firstName',
                    'lastName',
                    'contactPhone',
                    'secondContactPhone',
                    'thirdContactPhone',
                    'email',
                    'secondEmail',
                    'thirdEmail',
                    'fax',
                    'postalCode',
                    'addressLine',
                    'countryId',
                    'cityId',
                    'citizenshipId',
                    'birthdate',
                    'birthPlace',
                    'travelPreferences',
                    'allergy',
                    'disability',
                    'company',
                    'position',
                    'emergencyContactPrefix',
                    'emergencyContactFirstName',
                    'emergencyContactLastName',
                    'emergencyContactPhone',
                    'emergencyContactEmail',
                    'comments',
                    'active',
                    'b2CId',
                    'managerId',
                    'language',
                    'avatar',
                    'stage',
                    'priority',
                    'memberLevel',
                    'expirationDate',
                    'externalMemberID',
                    'labels',
                    'middleName',
                ]

                const person = {}
                for (const key of necessaryKeys) {
                    person[key] = this.person[key]
                }
                Object.assign(person, {
                    avatar: this.avatar ? this.avatar.split(',')[1] : this.person?.avatar,
                    labels: this.labels.join(','),
                })
                clean(person)
                if (person.disability) {
                    if (!person?.extraInfos) person.extraInfos = []
                    person.extraInfos.push({type: 'DISABILITY', info: person.disability})
                    delete person.disability
                }
                if (person.allergy) {
                    if (!person?.extraInfos) person.extraInfos = []
                    person.extraInfos.push({type: 'ALLERGY', info: person.allergy})
                    delete person.allergy
                }
                if (this.password) {
                    Object.assign(person, {password: this.password})
                }
                if (this.person.personId) {
                    await this.$api.privateClients.put(this.person.personId, person)
                } else {
                    const rs = await this.$api.privateClients.post(person)
                    await this.$router.push({name: 'companyClient', params: {id: rs.id}})
                }
                this.$toast.success(this.$t('messages.success.user_saved'))
            } catch (e) {
                if (e?.errors) {
                    e.errors.forEach(error => {
                        if (error.errorType === 'validation') {
                            this.$toast.error(error.message)
                        } else {
                            this.$toast.error(this.$t('error_message.save_error'))
                        }
                    })
                } else {
                    this.$toast.error(this.$t('error_message.save_error'))
                }
            } finally {
                this.isLoading = false
            }
        }

        async loadCities() {
            try {
                const rs = await this.$api.locations.get({
                    limitCities: -1,
                    orderBy: 'ALPHABET',
                    countryId: this.person.countryId,
                    applyRestriction: false,
                })

                this.cities = rs.cities
            } catch (e) {
                this.cities = []
            }

            // Always resolve the saved city, even when its country differs from the
            // selected one or the country-filtered request fails. Without this the
            // autocomplete shows the raw numeric cityId instead of the city name.
            if (this.person.cityId && !this.cities.some((c) => c.id === this.person.cityId)) {
                try {
                    const {cities: [current]} = await this.$api.locations.get({
                        id: this.person.cityId,
                        limitCities: 1,
                    })
                    if (current) this.cities = [current, ...this.cities]
                } catch (e) {}
            }
        }

        get countries() {
            return runtimeStore.countries
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get hideCitizenship() {
            return false
        }

        removeLabel(item) {
            this.labels = this.labels.filter(label => item !== label)
        }

        get isShowMiddleName() {
            return runtimeStore.getCRMOption('asp.option.show.person.middle.name')
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }
    }

    export default ClientForm
</script>
